/**
 * Transient Scrollbar Utility
 * Manages overlay scrollbar behavior with fade in/out effects
 */

class TransientScrollbar {
  constructor() {
    this.scrollTimer = null;
    this.isScrolling = false;
    this.fadeDelay = 2000; // 2 seconds delay before hiding

    this.init();
  }

  init() {
    // Add scrolling class during scroll
    this.handleScroll = this.handleScroll.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);

    // Add event listeners
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    document.body.addEventListener("mousemove", this.handleMouseMove);
    document.body.addEventListener("mouseleave", this.handleMouseLeave);

    // Initial state
    document.body.classList.remove("scrolling");
    document.documentElement.classList.remove("scrolling");
  }

  handleScroll() {
    // Show scrollbar immediately when scrolling
    if (!this.isScrolling) {
      this.isScrolling = true;
      document.body.classList.add("scrolling");
      document.documentElement.classList.add("scrolling"); // For Firefox
    }

    // Clear existing timer
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }

    // Set timer to hide scrollbar after delay
    this.scrollTimer = setTimeout(() => {
      this.isScrolling = false;
      document.body.classList.remove("scrolling");
      document.documentElement.classList.remove("scrolling"); // For Firefox
    }, this.fadeDelay);
  }

  handleMouseMove(event) {
    // Check if mouse is near the right edge (scrollbar area)
    const scrollbarZone = window.innerWidth - 20; // 20px from right edge

    if (event.clientX >= scrollbarZone) {
      document.body.classList.add("scrolling");
      document.documentElement.classList.add("scrolling"); // For Firefox

      // Clear fade timer when hovering over scrollbar
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }
    }
  }

  handleMouseLeave() {
    // Start fade timer when mouse leaves the page
    if (!this.isScrolling) {
      this.scrollTimer = setTimeout(() => {
        document.body.classList.remove("scrolling");
        document.documentElement.classList.remove("scrolling"); // For Firefox
      }, 500); // Shorter delay for mouse leave
    }
  }

  // Method to manually show scrollbar
  show() {
    document.body.classList.add("scrolling");
    document.documentElement.classList.add("scrolling"); // For Firefox
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }
  }

  // Method to manually hide scrollbar
  hide() {
    document.body.classList.remove("scrolling");
    document.documentElement.classList.remove("scrolling"); // For Firefox
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }
  }

  // Cleanup method
  destroy() {
    window.removeEventListener("scroll", this.handleScroll);
    document.body.removeEventListener("mousemove", this.handleMouseMove);
    document.body.removeEventListener("mouseleave", this.handleMouseLeave);

    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }

    document.body.classList.remove("scrolling");
    document.documentElement.classList.remove("scrolling"); // For Firefox
  }
}

// Initialize transient scrollbar
let transientScrollbar = null;

export const initTransientScrollbar = () => {
  if (!transientScrollbar) {
    transientScrollbar = new TransientScrollbar();
  }
  return transientScrollbar;
};

export const destroyTransientScrollbar = () => {
  if (transientScrollbar) {
    transientScrollbar.destroy();
    transientScrollbar = null;
  }
};

export default TransientScrollbar;
